import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Button } from '@/design-system/Button'
import { Badge, Card } from '@/design-system/primitives'
import { ApiError } from '@/lib/apiClient'
import { cn } from '@/lib/cn'
import { formatDateTime } from '@/lib/datetime'
import { formatMoney } from '@/lib/money'

import { useAdminMutation, useAdminQuery } from '../api'
import { useAdminContext } from '../session'
import type { AdminSetting, CompositionLine, DrinkOption, TableRequest } from '../types'
import { TableRequestFollowUp } from './TableRequestFollowUp'

/**
 * Une table personnalisée : la table affichée que le client a modifiée.
 *
 * Ce que l'équipe doit voir d'un coup d'œil : la table d'origine et son prix
 * affiché, ce qui a changé (retiré, ajouté, modifié), la note, le contact.
 * Puis trois gestes : ajuster la composition, **« Valider et fixer le prix »**
 * (la commande part au règlement, le client reçoit le prix et de quoi
 * payer), ou refuser. Validée, la suite est celle de toute table : convives,
 * « Marquer payé », « Envoyer la confirmation ».
 */
const TONES: Record<string, 'neutral' | 'champagne' | 'success' | 'ember'> = {
  new: 'champagne',
  quoted: 'neutral',
  accepted: 'success',
  declined: 'ember',
  cancelled: 'neutral',
}

export function TableCustomizationCard({
  request,
  busy,
  onDecline,
  onRemove,
}: {
  request: TableRequest
  busy: boolean
  onDecline: (reason: string) => void
  onRemove: () => void
}) {
  const { t, i18n } = useTranslation()
  const { event, can } = useAdminContext()
  const timeZone = event?.timezone ?? 'UTC'
  const [mode, setMode] = useState<'idle' | 'adjust' | 'validate' | 'decline' | 'remove'>('idle')
  const [reason, setReason] = useState('')
  const money = (minor: number) =>
    formatMoney({ amount_minor: minor, currency: request.currency }, i18n.language)
  const open = request.status === 'new'
  const canWrite = can('tables.reservation.update')
  const canCancel = can('tables.reservation.cancel')
  const origin = [request.table_type_label, request.package_label].filter(Boolean).join(' · ')
  const adjusted = request.items_changed_at !== null

  return (
    <Card className={cn(open && 'border-l-4 border-l-champagne-400')}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="break-words text-content-primary">
            {request.full_name}
            <span className="ml-2 text-sm text-content-muted">
              {t('admin.tableRequests.people', { count: request.party_size })}
            </span>
          </p>
          <p className="mt-0.5 break-words text-caption text-content-muted">
            {request.email}
            {request.phone ? ` · WhatsApp ${request.phone}` : ''} ·{' '}
            {new Date(request.created_at).toLocaleDateString(i18n.language)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge tone="champagne">{t('admin.tableRequests.customization.badge')}</Badge>
          <Badge tone={TONES[request.status] ?? 'neutral'}>
            {request.status === 'accepted'
              ? t('admin.tableRequests.customization.validated')
              : t(`admin.tableRequests.statuses.${request.status}`)}
          </Badge>
        </div>
      </div>

      <p className="mt-3 text-sm text-content-secondary">
        {t('admin.tableRequests.customization.origin', { table: origin || '—' })}
        {request.base_price_minor !== null ? (
          <span className="tabular-nums">
            {' '}
            · {t('admin.tableRequests.customization.shownPrice', {
              price: money(request.base_price_minor),
            })}
          </span>
        ) : null}
      </p>

      <CompositionDiff lines={request.composition} className="mt-3" />
      {adjusted ? (
        <div className="mt-2 text-caption text-content-muted">
          <p className="text-champagne-300">
            {t('admin.tableRequests.customization.adjusted', {
              date: formatDateTime(request.items_changed_at ?? '', i18n.language, timeZone),
              who: request.items_changed_by_label ?? '—',
            })}
          </p>
          <p className="mt-1">
            {t('admin.tableRequests.customization.asked')}{' '}
            {request.requested
              .filter((line) => line.quantity > 0)
              .map((line) => `${line.quantity} × ${line.label}`)
              .join(' · ') || '—'}
          </p>
        </div>
      ) : null}

      {request.notes ? (
        <p className="mt-3 whitespace-pre-line break-words rounded-md bg-bg-overlay px-3 py-2 text-sm text-content-secondary">
          {request.notes}
        </p>
      ) : null}

      {request.quoted_price_minor !== null && request.status === 'accepted' ? (
        <p className="mt-3 text-sm text-content-primary">
          {t('admin.tableRequests.customization.priceSet', {
            price: money(request.quoted_price_minor),
            who: request.quoted_by_label ?? '—',
          })}
        </p>
      ) : null}
      {request.decline_reason ? (
        <p className="mt-2 text-sm text-ember-300">« {request.decline_reason} »</p>
      ) : null}
      {request.order_number ? (
        <div className="mt-2 space-y-1 text-sm text-content-secondary">
          <p>
            {t('admin.tableRequests.order')}{' '}
            <Link
              to={`../commandes?q=${encodeURIComponent(request.order_number)}`}
              relative="path"
              className="tabular-nums text-champagne-300 underline"
            >
              {request.order_number}
            </Link>
          </p>
          {request.payment_choice === 'cash_at_door' || request.payment_choice === 'paypal_at_door' ? (
            <p>{t(`admin.tableRequests.customization.payment.${request.payment_choice}`)}</p>
          ) : request.payment_reference ? (
            <p className="break-words">
              {t('admin.tableRequests.customization.reference', {
                reference: request.payment_reference,
              })}
              {request.payment_link ? (
                <>
                  {' · '}
                  <a href={request.payment_link} target="_blank" rel="noreferrer" className="underline">
                    {t('admin.tableRequests.customization.paymentLink')}
                  </a>
                </>
              ) : null}
            </p>
          ) : null}
        </div>
      ) : null}

      {mode === 'adjust' ? (
        <CompositionEditor request={request} onDone={() => setMode('idle')} />
      ) : mode === 'validate' ? (
        <ValidateForm request={request} onDone={() => setMode('idle')} />
      ) : mode === 'decline' ? (
        <div className="mt-4 space-y-3">
          <label className="block text-caption text-content-muted">
            {t('admin.tableRequests.declineReason')}
            <input
              value={reason}
              maxLength={1000}
              onChange={(changeEvent) => setReason(changeEvent.target.value)}
              className={INPUT}
            />
          </label>
          <p className="text-caption text-content-muted">
            {t('admin.tableRequests.customization.declineHint')}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={busy || reason.trim().length < 3}
              onClick={() => {
                onDecline(reason.trim())
                setMode('idle')
              }}
            >
              {t('admin.tableRequests.confirmDecline')}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setMode('idle')}>
              {t('admin.project.cancel')}
            </Button>
          </div>
        </div>
      ) : mode === 'remove' ? (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="text-sm text-content-secondary">
            {request.order_number
              ? t('admin.tableRequests.removeConfirmOrder', { order: request.order_number })
              : t('admin.tableRequests.removeConfirm')}
          </span>
          <Button
            variant="champagne"
            size="sm"
            disabled={busy}
            onClick={() => {
              onRemove()
              setMode('idle')
            }}
          >
            {t('admin.project.deleteYes')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setMode('idle')}>
            {t('admin.project.cancel')}
          </Button>
        </div>
      ) : (
        <div className="mt-4 flex flex-wrap gap-2">
          {open && canWrite ? (
            <>
              <Button size="sm" disabled={busy} onClick={() => setMode('validate')}>
                {t('admin.tableRequests.customization.validate')}
              </Button>
              <Button variant="outline" size="sm" disabled={busy} onClick={() => setMode('adjust')}>
                {t('admin.tableRequests.customization.adjust')}
              </Button>
            </>
          ) : null}
          {open && canCancel ? (
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setMode('decline')}>
              {t('admin.tableRequests.decline')}
            </Button>
          ) : null}
          {canCancel ? (
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setMode('remove')}>
              {t('admin.tableRequests.remove')}
            </Button>
          ) : null}
        </div>
      )}

      {request.status === 'accepted' ? <TableRequestFollowUp request={request} /> : null}
    </Card>
  )
}

/** Ce qui a changé par rapport à la table d'origine, ligne par ligne. */
function CompositionDiff({ lines, className }: { lines: CompositionLine[]; className?: string }) {
  const { t } = useTranslation()
  if (lines.length === 0) {
    return (
      <p className={cn('text-sm text-content-muted', className)}>
        {t('admin.tableRequests.customization.noBottles')}
      </p>
    )
  }
  return (
    <ul className={cn('divide-y divide-line-subtle rounded-md border border-line-subtle', className)}>
      {lines.map((line) => (
        <li
          key={line.drink_item_id}
          className={cn(
            'flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 py-2 text-sm',
            line.change === 'added' && 'bg-champagne-400/5',
            line.change === 'removed' && 'bg-ember-400/5',
          )}
        >
          <span
            className={cn(
              'min-w-0 break-words text-content-primary',
              line.change === 'removed' && 'text-content-muted line-through',
            )}
          >
            {line.label}
          </span>
          <span className="flex items-center gap-2 tabular-nums">
            {line.change === 'unchanged' ? (
              <span className="text-content-secondary">{line.quantity}</span>
            ) : (
              <>
                <span className="text-content-muted">{line.base_quantity}</span>
                <span aria-hidden className="text-content-muted">→</span>
                <span className="text-content-primary">{line.quantity}</span>
                <Badge tone={line.change === 'removed' ? 'ember' : 'champagne'}>
                  {t(`admin.tableRequests.customization.change.${line.change}`)}
                </Badge>
              </>
            )}
          </span>
        </li>
      ))}
    </ul>
  )
}

/** L'équipe ajuste avant de valider. La demande du client reste figée. */
function CompositionEditor({ request, onDone }: { request: TableRequest; onDone: () => void }) {
  const { t, i18n } = useTranslation()
  const { eventId } = useAdminContext()
  const options = useAdminQuery<DrinkOption[]>(
    ['admin-reservation-drinks', eventId],
    `/admin/events/${eventId}/reservations/drink-options`,
    eventId,
  )
  const save = useAdminMutation<
    TableRequest,
    { party_size: number; items: { drink_item_id: string; quantity: number }[]; reason: string | null }
  >(
    'put',
    () => `/admin/events/${eventId}/table-requests/${request.id}/composition`,
    eventId,
    [['admin-table-requests', eventId]],
  )
  const [partySize, setPartySize] = useState(String(request.party_size))
  const [lines, setLines] = useState(
    request.composition
      .filter((line) => line.quantity > 0)
      .map((line) => ({
        drink_item_id: line.drink_item_id,
        label: line.label,
        quantity: String(line.quantity),
      })),
  )
  const [search, setSearch] = useState('')
  const [reason, setReason] = useState('')

  const size = Number.parseInt(partySize, 10)
  const min = request.capacity_min ?? 1
  const max = request.capacity_max ?? 200
  const sizeValid = Number.isFinite(size) && size >= min && size <= max
  const parsed = lines.map((line) => ({
    drink_item_id: line.drink_item_id,
    quantity: Number.parseInt(line.quantity, 10),
  }))
  const linesValid = parsed.every(
    (line) => Number.isFinite(line.quantity) && line.quantity >= 1 && line.quantity <= 100,
  )

  const taken = new Set(lines.map((line) => line.drink_item_id))
  const needle = search.trim().toLocaleLowerCase(i18n.language)
  const groups: { label: string; items: DrinkOption[] }[] = []
  for (const option of options.data ?? []) {
    if (
      needle &&
      !option.label.toLocaleLowerCase(i18n.language).includes(needle) &&
      !(option.category_label ?? '').toLocaleLowerCase(i18n.language).includes(needle)
    )
      continue
    const label = option.category_label ?? t('admin.reservations.detail.otherDrinks')
    const group = groups.find((entry) => entry.label === label)
    if (group) group.items.push(option)
    else groups.push({ label, items: [option] })
  }

  return (
    <div className="mt-4 space-y-3 rounded-md border border-line-subtle p-3">
      <label className="block text-caption text-content-muted">
        {t('admin.reservations.detail.partySize', { min, max })}
        <input
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          value={partySize}
          onChange={(changeEvent) => setPartySize(changeEvent.target.value)}
          className={cn(INPUT, 'tabular-nums sm:w-32', !sizeValid && 'border-state-danger')}
        />
      </label>
      {lines.length === 0 ? (
        <p className="text-sm text-content-muted">{t('admin.reservations.detail.noBottles')}</p>
      ) : (
        <ul className="space-y-2">
          {lines.map((line, index) => (
            <li key={line.drink_item_id} className="flex items-center gap-2">
              <span className="min-w-0 flex-1 break-words text-sm text-content-primary">
                {line.label}
              </span>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={100}
                aria-label={t('admin.reservations.detail.quantityOf', { drink: line.label })}
                value={line.quantity}
                onChange={(changeEvent) =>
                  setLines((current) =>
                    current.map((entry, position) =>
                      position === index ? { ...entry, quantity: changeEvent.target.value } : entry,
                    ),
                  )
                }
                className={cn(INPUT, 'mt-0 w-20 shrink-0 tabular-nums')}
              />
              <button
                type="button"
                aria-label={t('admin.reservations.detail.removeDrinkNamed', { drink: line.label })}
                onClick={() =>
                  setLines((current) => current.filter((_, position) => position !== index))
                }
                className="h-10 w-10 shrink-0 rounded-md border border-line text-content-muted"
              >
                −
              </button>
            </li>
          ))}
        </ul>
      )}
      <label className="block text-caption text-content-muted">
        {t('admin.reservations.detail.addDrink')}
        <input
          type="search"
          value={search}
          placeholder={t('admin.reservations.detail.searchDrink')}
          onChange={(changeEvent) => setSearch(changeEvent.target.value)}
          className={INPUT}
        />
      </label>
      <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
        {groups.map((group) => (
          <div key={group.label}>
            <p className="text-caption text-content-muted">{group.label}</p>
            <ul className="mt-1 flex flex-wrap gap-1.5">
              {group.items.map((option) => (
                <li key={option.id}>
                  <button
                    type="button"
                    disabled={taken.has(option.id)}
                    onClick={() =>
                      setLines((current) => [
                        ...current,
                        { drink_item_id: option.id, label: option.label, quantity: '1' },
                      ])
                    }
                    className="min-h-9 rounded-md border border-line px-2.5 py-1 text-left text-sm text-content-primary disabled:opacity-40"
                  >
                    + {option.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <label className="block text-caption text-content-muted">
        {t('admin.tableRequests.customization.adjustReason')}
        <input
          value={reason}
          maxLength={500}
          onChange={(changeEvent) => setReason(changeEvent.target.value)}
          className={INPUT}
        />
      </label>
      {save.error instanceof ApiError ? (
        <p className="text-sm text-state-danger">{save.error.message}</p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          disabled={!sizeValid || !linesValid || save.isPending}
          onClick={() =>
            save.mutate(
              { party_size: size, items: parsed, reason: reason.trim() || null },
              { onSuccess: onDone },
            )
          }
        >
          {t('admin.reservations.detail.save')}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          {t('admin.project.cancel')}
        </Button>
      </div>
    </div>
  )
}

const DOOR_SETTINGS: Record<string, string> = {
  cash_at_door: 'payment.allow_cash_at_door',
  paypal_at_door: 'payment.allow_paypal_at_door',
}

/** « Valider et fixer le prix » : le montant exact, le moyen de paiement, le message. */
function ValidateForm({ request, onDone }: { request: TableRequest; onDone: () => void }) {
  const { t, i18n } = useTranslation()
  const { eventId } = useAdminContext()
  const settings = useAdminQuery<AdminSetting[]>(
    ['admin-settings', eventId],
    `/admin/events/${eventId}/settings`,
    eventId,
  )
  const validate = useAdminMutation<
    TableRequest,
    {
      price_minor: number
      message: string | null
      includes_admission: boolean
      name: string | null
      payment_choice: string
    }
  >(
    'post',
    () => `/admin/events/${eventId}/table-requests/${request.id}/validate`,
    eventId,
    [['admin-table-requests', eventId], ['admin-orders', eventId], ['admin-reservations', eventId]],
  )
  const [price, setPrice] = useState(
    request.base_price_minor !== null ? (request.base_price_minor / 100).toFixed(2) : '',
  )
  const [message, setMessage] = useState('')
  const [admission, setAdmission] = useState(request.includes_admission)
  const [name, setName] = useState('')
  const [choice, setChoice] = useState('paypal_link')

  // Le règlement à l'entrée n'est proposé que si les réglages de vente
  // l'ouvrent : le serveur le refuserait sinon.
  const values = new Map((settings.data ?? []).map((row) => [row.code, row.value]))
  const choices = ['paypal_link', ...Object.keys(DOOR_SETTINGS).filter((key) => values.get(DOOR_SETTINGS[key] ?? '') === true)]

  // Au centime : « 555,5 » ou « 555.50 » donnent 55 550, jamais 55 549.
  const cleaned = price.trim().replace(/\s/g, '').replace(',', '.')
  const valid = /^\d+(\.\d{1,2})?$/.test(cleaned)
  const minor = valid ? Math.round(Number(cleaned) * 100) : null

  return (
    <div className="mt-4 space-y-3 rounded-md border border-line-subtle p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-caption text-content-muted">
          {t('admin.tableRequests.price', { currency: request.currency })}
          <input
            inputMode="decimal"
            value={price}
            onChange={(changeEvent) => setPrice(changeEvent.target.value)}
            className={cn(INPUT, 'tabular-nums', price && !valid && 'border-state-danger')}
          />
        </label>
        <label className="text-caption text-content-muted">
          {t('admin.tableRequests.customization.paymentChoice')}
          <select
            value={choice}
            onChange={(changeEvent) => setChoice(changeEvent.target.value)}
            className={INPUT}
          >
            {choices.map((value) => (
              <option key={value} value={value}>
                {t(`admin.tableRequests.customization.choices.${value}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-caption text-content-muted">
          {t('admin.tableRequests.groupName')}
          <input
            value={name}
            maxLength={120}
            placeholder={t('admin.tableRequests.groupNamePlaceholder')}
            onChange={(changeEvent) => setName(changeEvent.target.value)}
            className={INPUT}
          />
        </label>
        <label className="text-caption text-content-muted">
          {t('admin.tableRequests.message')}
          <input
            value={message}
            maxLength={2000}
            placeholder={t('admin.tableRequests.customization.messagePlaceholder')}
            onChange={(changeEvent) => setMessage(changeEvent.target.value)}
            className={INPUT}
          />
        </label>
      </div>
      <label className="flex items-start gap-2 text-sm text-content-secondary">
        <input
          type="checkbox"
          checked={admission}
          onChange={() => setAdmission((value) => !value)}
          className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--ember-500)]"
        />
        <span>{t('admin.tableRequests.includesAdmission')}</span>
      </label>
      {minor !== null ? (
        <p className="text-sm text-content-primary">
          {t('admin.tableRequests.customization.validateConfirm', {
            price: formatMoney({ amount_minor: minor, currency: request.currency }, i18n.language),
            email: request.email,
          })}
        </p>
      ) : null}
      {validate.error instanceof ApiError ? (
        <p className="text-sm text-state-danger">{validate.error.message}</p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          disabled={minor === null || validate.isPending}
          onClick={() =>
            minor !== null &&
            validate.mutate(
              {
                price_minor: minor,
                message: message.trim() || null,
                includes_admission: admission,
                name: name.trim() || null,
                payment_choice: choice,
              },
              { onSuccess: onDone },
            )
          }
        >
          {validate.isPending
            ? t('admin.tableRequests.sending')
            : t('admin.tableRequests.customization.validateYes')}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          {t('admin.project.cancel')}
        </Button>
      </div>
    </div>
  )
}

const INPUT =
  'mt-1 h-10 w-full min-w-0 rounded-md border border-line bg-bg-base px-3 text-sm text-content-primary'
